import React, { useEffect, useRef, useState } from "react";
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Italic,
  Eye,
  Pencil,
  Link,
  List,
  ListOrdered,
  Palette,
  Redo2,
  RemoveFormatting,
  Save,
  Table,
  Underline,
  Undo2,
  X,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import { Dialog } from "../../ui";
import {
  buildInlineTable,
  containsRichTextHtml,
  escapeHtml,
  normalizeRichTextHtml,
  prepareRichTextForEditing,
  richTextToPlainText,
} from "./richTextUtils";
import "./AboutItemEditor.css";

const ToolbarButton = ({ label, onClick, children }) => (
  <button
    type="button"
    className="about-editor-tool"
    aria-label={label}
    title={label}
    onMouseDown={(event) => event.preventDefault()}
    onClick={onClick}
  >
    {children}
  </button>
);

const AboutItemEditor = ({ open, value, onClose, onSave, title = "About Item" }) => {
  const editorRef = useRef(null);
  const selectionRef = useRef(null);
  const colorInputRef = useRef(null);
  const usedFormattingRef = useRef(false);
  const [hasHtmlContent, setHasHtmlContent] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");

  useEffect(() => {
    if (!open || !editorRef.current) return;
    editorRef.current.innerHTML = prepareRichTextForEditing(value);
    const usesHtml = containsRichTextHtml(value);
    usedFormattingRef.current = usesHtml;
    setHasHtmlContent(usesHtml);
    setPreviewing(false);
    editorRef.current.focus();
  }, [open, value]);

  const rememberSelection = () => {
    const selection = window.getSelection();
    if (!selection?.rangeCount || !editorRef.current?.contains(selection.anchorNode)) return;
    selectionRef.current = selection.getRangeAt(0).cloneRange();
  };

  const restoreSelection = () => {
    editorRef.current?.focus();
    if (!selectionRef.current) return;
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(selectionRef.current);
  };

  const runCommand = (command, commandValue = null) => {
    restoreSelection();
    document.execCommand(command, false, commandValue);
    usedFormattingRef.current = true;
    setHasHtmlContent(true);
    rememberSelection();
  };

  const insertLink = () => {
    rememberSelection();
    const url = window.prompt("Enter link URL (https://...)", "https://");
    if (!url) return;
    const safeUrl = /^(https?:|mailto:|tel:)/i.test(url.trim()) ? url.trim() : `https://${url.trim()}`;
    restoreSelection();
    const selection = window.getSelection();
    if (selection?.isCollapsed) {
      document.execCommand(
        "insertHTML",
        false,
        `<a href="${escapeHtml(safeUrl)}" target="_blank" rel="noopener noreferrer" style="color: #1565c0; text-decoration: underline;">${escapeHtml(safeUrl)}</a>`
      );
    } else {
      document.execCommand("createLink", false, safeUrl);
      const link = selection?.anchorNode?.parentElement?.closest?.("a");
      if (link) {
        link.style.color = "#1565c0";
        link.style.textDecoration = "underline";
      }
    }
    usedFormattingRef.current = true;
    setHasHtmlContent(true);
    rememberSelection();
  };

  const insertTable = () => {
    rememberSelection();
    const rows = window.prompt("Number of rows (1-10)", "2");
    if (rows === null) return;
    const columns = window.prompt("Number of columns (1-8)", "2");
    if (columns === null) return;
    restoreSelection();
    document.execCommand("insertHTML", false, buildInlineTable(rows, columns));
    usedFormattingRef.current = true;
    setHasHtmlContent(true);
    rememberSelection();
  };

  const saveContent = () => {
    if (!editorRef.current) return;
    const content = usedFormattingRef.current
      ? normalizeRichTextHtml(editorRef.current.innerHTML)
      : richTextToPlainText(editorRef.current);
    onSave(content);
  };

  const togglePreview = () => {
    if (!previewing && editorRef.current) {
      setPreviewHtml(normalizeRichTextHtml(editorRef.current.innerHTML));
    }
    setPreviewing((current) => !current);
  };

  return (
    <Dialog open={open} fullScreen onClose={onClose} aria-labelledby="about-item-editor-title">
      <div className="about-editor-shell">
        <header className="about-editor-header">
          <h2 id="about-item-editor-title">{title}</h2>
          <div className="about-editor-header-actions">
            <button type="button" aria-label="Save about item" title="Save" onClick={saveContent}>
              <Save aria-hidden="true" />
            </button>
            <button type="button" aria-label="Close editor" title="Close without saving" onClick={onClose}>
              <X aria-hidden="true" />
            </button>
          </div>
        </header>

        <div className="about-editor-toolbar" role="toolbar" aria-label="Text formatting">
          {hasHtmlContent && (
            <>
              <ToolbarButton label={previewing ? "Back to editor" : "Preview HTML content"} onClick={togglePreview}>
                {previewing ? <Pencil /> : <Eye />}
              </ToolbarButton>
              <span className="about-editor-divider" />
            </>
          )}
          {!previewing && (
            <>
          <ToolbarButton label="Bold" onClick={() => runCommand("bold")}><Bold /></ToolbarButton>
          <ToolbarButton label="Italic" onClick={() => runCommand("italic")}><Italic /></ToolbarButton>
          <ToolbarButton label="Underline" onClick={() => runCommand("underline")}><Underline /></ToolbarButton>
          <ToolbarButton label="Normal text" onClick={() => runCommand("removeFormat")}><RemoveFormatting /></ToolbarButton>
          <span className="about-editor-divider" />
          <ToolbarButton label="Increase text size" onClick={() => runCommand("increaseFontSize")}><ZoomIn /></ToolbarButton>
          <ToolbarButton label="Decrease text size" onClick={() => runCommand("decreaseFontSize")}><ZoomOut /></ToolbarButton>
          <label
            className="about-editor-tool"
            aria-label="Change text color"
            title="Change text color"
            onMouseDown={rememberSelection}
          >
            <Palette aria-hidden="true" />
            <input
              ref={colorInputRef}
              type="color"
              defaultValue="#1f2937"
              onChange={(event) => runCommand("foreColor", event.target.value)}
            />
          </label>
          <span className="about-editor-divider" />
          <ToolbarButton label="Align left" onClick={() => runCommand("justifyLeft")}><AlignLeft /></ToolbarButton>
          <ToolbarButton label="Align center" onClick={() => runCommand("justifyCenter")}><AlignCenter /></ToolbarButton>
          <ToolbarButton label="Align right" onClick={() => runCommand("justifyRight")}><AlignRight /></ToolbarButton>
          <ToolbarButton label="Bulleted list" onClick={() => runCommand("insertUnorderedList")}><List /></ToolbarButton>
          <ToolbarButton label="Numbered list" onClick={() => runCommand("insertOrderedList")}><ListOrdered /></ToolbarButton>
          <ToolbarButton label="Insert link" onClick={insertLink}><Link /></ToolbarButton>
          <ToolbarButton label="Insert table" onClick={insertTable}><Table /></ToolbarButton>
          <span className="about-editor-divider" />
          <ToolbarButton label="Undo" onClick={() => runCommand("undo")}><Undo2 /></ToolbarButton>
          <ToolbarButton label="Redo" onClick={() => runCommand("redo")}><Redo2 /></ToolbarButton>
            </>
          )}
        </div>

        <main className="about-editor-paper-wrap">
          <div
            ref={editorRef}
            className={`about-editor-paper${previewing ? " about-editor-hidden" : ""}`}
            contentEditable
            suppressContentEditableWarning
            role="textbox"
            aria-multiline="true"
            aria-label="About item content"
            data-placeholder="Write product details here..."
            onInput={() => {
              rememberSelection();
              if (editorRef.current && containsRichTextHtml(editorRef.current.innerHTML)) {
                usedFormattingRef.current = true;
                setHasHtmlContent(true);
              }
            }}
            onKeyUp={rememberSelection}
            onMouseUp={rememberSelection}
            onPaste={(event) => {
              if (event.clipboardData?.getData("text/html")) {
                usedFormattingRef.current = true;
                setHasHtmlContent(true);
              }
            }}
          />
          {previewing && (
            <section
              className="about-editor-paper about-editor-preview"
              aria-label="About item preview"
              dangerouslySetInnerHTML={{ __html: previewHtml }}
            />
          )}
        </main>
      </div>
    </Dialog>
  );
};

export default AboutItemEditor;
